/**
 * Question dependency graph. Parent ids are inputs: a child cannot run until
 * each parent has an answer. Shared by Temporal extraction and AI report plans.
 */
export interface QuestionWithParents {
  id: string;
  parentIds: string[];
}

const CYCLE_ERROR_MESSAGE = 'Cycle detected in question dependencies';

export function getQuestionDependencyCycleErrorMessage() {
  return CYCLE_ERROR_MESSAGE;
}

/**
 * True when walking parent (input) links from any node returns to a node
 * already on the stack.
 */
export function getHasQuestionDependencyCycle(nodes: QuestionWithParents[]): boolean {
  try {
    assertNoQuestionDependencyCycle(nodes);
    return false;
  } catch (error) {
    if (error instanceof Error && error.message === CYCLE_ERROR_MESSAGE) return true;
    throw error;
  }
}

export function assertNoQuestionDependencyCycle(nodes: QuestionWithParents[]): void {
  const stack = new Set<string>();
  const visited = new Set<string>();
  const nodeMap = new Map<string, QuestionWithParents>(nodes.map(node => [node.id, node]));
  for (const node of nodes) detectCycle(node, visited, stack, nodeMap);
}

function detectCycle(
  node: QuestionWithParents,
  visited: Set<string>,
  stack: Set<string>,
  nodeMap: Map<string, QuestionWithParents>
) {
  if (stack.has(node.id)) throw new Error(CYCLE_ERROR_MESSAGE);
  if (visited.has(node.id)) return;

  stack.add(node.id);
  for (const parentId of node.parentIds) {
    const parent = nodeMap.get(parentId);
    if (parent) detectCycle(parent, visited, stack, nodeMap);
  }

  visited.add(node.id);
  stack.delete(node.id);
}
